import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/* ──────────────────────────────────────────────────────────────────────
   AskStewie Design System — Shared Primitives (P85)
   - Card, Badge, StatusPill, RiskBar, ConfidenceMeter,
     CollapsibleSection, ActionTile, SectionHeader
   - All use CSS variables from /app/frontend/src/styles/design_tokens.css
   ────────────────────────────────────────────────────────────────────── */

export function Card({ children, interactive = false, glow = null, className = '', ...rest }) {
  const cls = ['asds-card', interactive && 'asds-card--interactive', glow && `asds-glow-${glow}`, className].filter(Boolean).join(' ');
  return <div className={cls} {...rest}>{children}</div>;
}

const TONES = {
  blue:   { color: 'var(--asds-blue)',   bg: 'var(--asds-blue-soft)',   border: 'rgba(37,99,235,0.35)' },
  cyan:   { color: 'var(--asds-cyan)',   bg: 'var(--asds-cyan-soft)',   border: 'rgba(34,211,238,0.35)' },
  purple: { color: 'var(--asds-purple)', bg: 'var(--asds-purple-soft)', border: 'rgba(167,139,250,0.35)' },
  yellow: { color: 'var(--asds-yellow)', bg: 'var(--asds-yellow-soft)', border: 'rgba(250,204,21,0.35)' },
  green:  { color: 'var(--asds-green)',  bg: 'var(--asds-green-soft)',  border: 'rgba(34,197,94,0.35)' },
  orange: { color: 'var(--asds-orange)', bg: 'var(--asds-orange-soft)', border: 'rgba(251,146,60,0.35)' },
  red:    { color: 'var(--asds-red)',    bg: 'var(--asds-red-soft)',    border: 'rgba(239,68,68,0.35)' },
  muted:  { color: 'var(--asds-text-secondary)', bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.10)' },
};

export function Badge({ children, tone = 'blue', icon: Icon, size = 'sm', className = '', ...rest }) {
  const t = TONES[tone] || TONES.blue;
  const fz = size === 'lg' ? '12px' : '10.5px';
  return (
    <span
      className={`asds-badge ${className}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: size === 'lg' ? '3px 10px' : '2px 8px',
        background: t.bg, color: t.color, border: `1px solid ${t.border}`,
        borderRadius: 'var(--asds-r-sm)',
        fontSize: fz, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
      }}
      {...rest}
    >
      {Icon && <Icon size={size === 'lg' ? 12 : 10} strokeWidth={2.4} />}
      {children}
    </span>
  );
}

export function StatusPill({ status }) {
  const map = {
    open:               { tone: 'green',  label: 'Open' },
    monitoring:         { tone: 'cyan',   label: 'Monitoring' },
    'steward review':   { tone: 'purple', label: 'Steward Review' },
    'grievance drafted':{ tone: 'orange', label: 'Grievance Drafted' },
    closed:             { tone: 'muted',  label: 'Closed' },
  };
  const key = (status || '').toLowerCase();
  const m = map[key] || { tone: 'muted', label: status || 'Unknown' };
  return <Badge tone={m.tone} size="sm">{m.label}</Badge>;
}

export function RiskBar({ value, label, tone, showValue = true, max = 100 }) {
  const pct = Math.max(0, Math.min(max, Number(value) || 0));
  const t = TONES[tone] || TONES.blue;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ color: 'var(--asds-text-primary)', fontSize: 13, fontWeight: 600 }}>{label}</span>
        {showValue && (
          <span style={{ color: t.color, fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(pct)}%
          </span>
        )}
      </div>
      <div style={{
        position: 'relative', height: 8, borderRadius: 'var(--asds-r-pill)',
        background: 'rgba(255,255,255,0.05)', overflow: 'hidden',
      }}>
        <div style={{
          width: `${(pct / max) * 100}%`, height: '100%',
          background: `linear-gradient(90deg, ${t.color} 0%, ${t.color}CC 100%)`,
          borderRadius: 'inherit',
          boxShadow: `0 0 12px ${t.border}`,
          transition: 'width var(--asds-dur-slow) var(--asds-ease-out)',
        }} />
      </div>
    </div>
  );
}

export function ConfidenceMeter({ value, label = 'Confidence' }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const tone = v >= 75 ? 'green' : v >= 50 ? 'cyan' : v >= 25 ? 'orange' : 'red';
  const t = TONES[tone];
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 11, color: 'var(--asds-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
        {label}
      </span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 9px', background: t.bg, border: `1px solid ${t.border}`,
        borderRadius: 'var(--asds-r-pill)', color: t.color,
        fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%', background: t.color,
          boxShadow: `0 0 8px ${t.color}`,
        }} />
        {Math.round(v)}%
      </span>
    </div>
  );
}

export function CollapsibleSection({ title, icon: Icon, tone = 'blue', defaultOpen = false, badge = null, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  const t = TONES[tone] || TONES.blue;
  return (
    <div
      className="asds-card"
      data-testid={testId}
      style={{ overflow: 'hidden' }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 16px', background: 'none', border: 'none',
          color: 'var(--asds-text-primary)', cursor: 'pointer', textAlign: 'left',
        }}
      >
        {Icon && (
          <span style={{
            width: 32, height: 32, display: 'grid', placeItems: 'center',
            background: t.bg, color: t.color, border: `1px solid ${t.border}`,
            borderRadius: 'var(--asds-r-md)', flexShrink: 0,
          }}>
            <Icon size={16} strokeWidth={2.2} />
          </span>
        )}
        <span style={{ flex: 1, fontSize: 14, fontWeight: 700, letterSpacing: '-0.005em' }}>{title}</span>
        {badge}
        {open
          ? <ChevronDown size={16} color="var(--asds-text-secondary)" />
          : <ChevronRight size={16} color="var(--asds-text-secondary)" />}
      </button>
      {open && (
        <div
          className="asds-anim-fade-up"
          style={{
            padding: '0 16px 16px',
            borderTop: '1px solid var(--asds-border)',
            paddingTop: 14,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function ActionTile({ icon: Icon, title, hint, tone = 'blue', onClick, disabled = false, testId }) {
  const t = TONES[tone] || TONES.blue;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="asds-card asds-card--interactive"
      style={{
        display: 'flex', flexDirection: 'column', gap: 8,
        padding: 14, minHeight: 92,
        background: 'var(--asds-bg-elevated)',
        color: 'var(--asds-text-primary)',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
      }}
    >
      <span style={{
        width: 36, height: 36, borderRadius: 'var(--asds-r-md)',
        background: t.bg, color: t.color, border: `1px solid ${t.border}`,
        display: 'grid', placeItems: 'center',
      }}>
        <Icon size={18} strokeWidth={2.3} />
      </span>
      <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{title}</span>
      {hint && <span style={{ fontSize: 11, color: 'var(--asds-text-secondary)', lineHeight: 1.3 }}>{hint}</span>}
    </button>
  );
}

export function SectionHeader({ eyebrow, title, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
      <div>
        {eyebrow && (
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
            color: 'var(--asds-text-secondary)', textTransform: 'uppercase', marginBottom: 4,
          }}>{eyebrow}</div>
        )}
        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--asds-text-primary)', letterSpacing: '-0.01em' }}>
          {title}
        </div>
      </div>
      {action}
    </div>
  );
}
